import { ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SITE_MANAGER_API_KEYS_URL } from '@/hooks/useControllers';

/**
 * Which kind of UniFi API key a new one must be, by how it will be used. The
 * kinds do not stand in for each other (measured 2026-09-22): a key from a
 * console's own Integrations page works only on that console's LAN API, and a
 * Site Manager key only through UniFi's cloud, for what it was scoped to when
 * it was made.
 */
export type UniFiKeySource =
  /** A Site Manager key that can list every console: a Site Manager directory. */
  | { kind: 'site-manager' }
  /** A Site Manager key for one site's Network app: a controller polled through UniFi's cloud. */
  | { kind: 'cloud-site'; site?: string }
  /**
   * The console's own key, from its Integrations page: a controller polled on
   * the LAN. `href` opens the console, as `hrefLabel` says.
   */
  | { kind: 'console'; href?: string; hrefLabel: string };

function OutLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-primary inline-flex items-center gap-0.5 hover:underline"
    >
      {children} <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function StepItems({ source }: { source: UniFiKeySource }) {
  const openSiteManager = (
    <li>
      Open <OutLink href={SITE_MANAGER_API_KEYS_URL}>Site Manager API Keys</OutLink> and choose{' '}
      <em>Create New API Key</em>.
    </li>
  );
  switch (source.kind) {
    case 'site-manager':
      return (
        <>
          {openSiteManager}
          <li>Keep <em>Site Manager</em> ticked, then create the key and paste it below.</li>
        </>
      );
    case 'cloud-site':
      return (
        <>
          {openSiteManager}
          <li>
            Untick <em>Site Manager</em>. Under Applications keep only <em>Network</em>.
          </li>
          <li>
            Under Sites choose only{' '}
            {source.site ? <strong>{source.site}</strong> : 'the site this controller reads'}, then
            create the key and paste it below.
          </li>
        </>
      );
    case 'console':
      return (
        <>
          <li>
            Open{' '}
            {source.href ? <OutLink href={source.href}>{source.hrefLabel}</OutLink> : source.hrefLabel}{' '}
            (Network → Settings → Control Plane → Integrations).
          </li>
          <li>
            Choose <em>Create New API Key</em>, then paste it below. A key made in Site
            Manager&apos;s API Keys page will not work here.
          </li>
        </>
      );
  }
}

/** Steps to make a new UniFi API key of the kind `source` needs, with links into UniFi. */
export function UniFiKeySteps({ source }: { source: UniFiKeySource }) {
  return (
    <ol className="list-decimal pl-5 space-y-1 text-xs text-muted-foreground">
      <StepItems source={source} />
    </ol>
  );
}

/**
 * The steps and the box to paste the key in. Shown below a `CredentialSelect`
 * while its "Paste a new key" choice is selected; the caller saves the key.
 */
export function UniFiKeyPanel({
  id,
  source,
  value,
  onChange,
  savedAs,
}: {
  /** For the paste box. */
  id?: string;
  source: UniFiKeySource;
  value: string;
  onChange: (key: string) => void;
  /** The name the key is saved under. */
  savedAs: string;
}) {
  return (
    <div className="rounded-lg border bg-muted/40 p-3 space-y-2 text-sm">
      <UniFiKeySteps source={source} />
      <Input
        id={id}
        type="password"
        autoComplete="off"
        value={value}
        onChange={(e) => { onChange(e.target.value); }}
        placeholder="Paste the API key"
      />
      <p className="text-xs text-muted-foreground">
        Saved as <strong>{savedAs}</strong>, and sent only to the gateways in the region you chose.
      </p>
    </div>
  );
}
